import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import TextField from "@components/ui/textfields/TextField";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";
import { vehiclesApi } from "@services/api/vehicles";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";

function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + "T12:00:00");
  return Math.floor(d.getTime() / 1000);
}

function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface LocationModalProps {
  reservation: VehicleReservation;
  onClose: () => void;
  onSave?: (updated: VehicleReservation) => void;
  onDelete?: () => void;
}

export default function LocationModal({
  reservation,
  onClose,
  onSave,
  onDelete,
}: LocationModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [form, setForm] = useState({ ...reservation });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgData?.id) return;
    vehiclesApi
      .list({ org_id: orgData.id, usage: "location" })
      .then(setVehicles)
      .catch(() => {});
  }, [orgData?.id]);

  const isMultiDay = form.startDate !== form.endDate;
  const update = <K extends keyof VehicleReservation>(key: K, value: VehicleReservation[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const vehicleOptions = vehicles.map((v) => ({
    value: v.id,
    label: `${v.name} — ${v.plate}`,
  }));

  const endTimeStr =
    !isMultiDay && form.startHour != null && form.durationHours != null
      ? hourToTimeStr(form.startHour + form.durationHours)
      : "";

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await vehiclesApi.updateReservation(reservation.id, {
        clientName: form.clientName,
        vehicleId: form.vehicleId,
        startDate: form.startDate,
        endDate: form.endDate,
        startHour: form.startHour,
        durationHours: form.durationHours,
        returnHour: form.returnHour,
        clientEmail: form.clientEmail,
        clientPhone: form.clientPhone,
        notes: form.notes,
        kmDepart: form.kmDepart,
        kmRetour: form.kmRetour,
      });
      onSave?.(updated);
      showNotification({ message: "Location mise à jour", type: "success" });
      onClose();
    } catch {
      showNotification({ message: "Erreur lors de la sauvegarde", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      title="Location"
      startIcon={<Icon1 name="car-reception" size={20} />}
      onClose={onClose}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {form.dossierId && (
          <Link
            to={`/dossier/${form.dossierId}`}
            onClick={onClose}
            className="self-start"
          >
            <SecondaryButton variant="small" startIcon={<Icon1 name="folder" size={14} />}>
              Voir le dossier
            </SecondaryButton>
          </Link>
        )}

        <TextField
          label="Client"
          value={form.clientName}
          onChange={(v) => update("clientName", v)}
        />

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Email"
              value={form.clientEmail || ""}
              onChange={(v) => update("clientEmail", v)}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="Téléphone"
              value={form.clientPhone || ""}
              onChange={(v) => update("clientPhone", v)}
            />
          </div>
        </div>

        <DropDownSearch
          label="Véhicule de location"
          options={vehicleOptions}
          value={form.vehicleId}
          onSelect={(v) => update("vehicleId", v as string)}
        />

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DatePicker
              label="Date début"
              seconds={isoToSeconds(form.startDate)}
              onChange={(s) => update("startDate", secondsToIso(s))}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label="Date fin"
              seconds={isoToSeconds(form.endDate)}
              onChange={(s) => update("endDate", secondsToIso(s))}
              minDateSeconds={isoToSeconds(form.startDate)}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TimePicker
              label="Heure de départ"
              value={form.startHour != null ? hourToTimeStr(form.startHour) : null}
              onChange={(timeStr) => {
                if (!timeStr) return;
                const newStart = timeStrToHour(timeStr);
                if (!isMultiDay && form.startHour != null && form.durationHours != null) {
                  const endHour = form.startHour + form.durationHours;
                  update("durationHours", Math.max(0.25, endHour - newStart));
                }
                update("startHour", newStart);
              }}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Heure de fin"
              value={
                isMultiDay
                  ? form.returnHour != null
                    ? hourToTimeStr(form.returnHour)
                    : null
                  : endTimeStr || null
              }
              onChange={(timeStr) => {
                if (!timeStr) return;
                const endHour = timeStrToHour(timeStr);
                if (isMultiDay) {
                  update("returnHour", endHour);
                } else {
                  const start = form.startHour ?? 8;
                  update("durationHours", Math.max(0.25, endHour - start));
                }
              }}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="KM départ"
              value={form.kmDepart || ""}
              onChange={(v) => update("kmDepart", v)}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="KM retour"
              value={form.kmRetour || ""}
              onChange={(v) => update("kmRetour", v)}
            />
          </div>
        </div>

        <TextField
          label="Notes"
          value={form.notes || ""}
          onChange={(v) => update("notes", v)}
        />
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-between gap-2">
        {onDelete ? (
          <SecondaryButton variant="small" danger onClick={onDelete}>
            Supprimer
          </SecondaryButton>
        ) : (
          <span />
        )}
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} disabled={saving} />
        </div>
      </div>
    </BaseModal>
  );
}
